import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import useCurrentLang from '@/hooks/useCurrentLang';
import useResize from '@/hooks/useResize';
import Footer from '@/pages/Index/Footer';
import { Box } from '@/layout/Box';

import {
  ActionPanel,
  AreaGrid,
  BannerImage,
  BenefitGrid,
  BenefitItem,
  BodyText,
  ConditionsGrid,
  ContactLink,
  ConsultationLead,
  ContentSection,
  Eyebrow,
  FeatureGrid,
  FeatureItem,
  FeatureLabel,
  FeatureValue,
  HeroSection,
  InfoGrid,
  InfoItem,
  Main,
  OfficeButton,
  PageContainer,
  SectionTitle,
  SplitSection,
  StepsList,
  TabButton,
  TabList,
  TextColumn,
  Title,
} from './style';

type HighlightItem = {
  value: string;
  label: string;
};

type VolunteerReason = {
  label: string;
  value: string;
};

type DescribedItem = {
  title: string;
  description: string;
};

const COPY_KEY = 'pages.index.FreeLegalLink';
const CONTACT_EMAIL = 'freelegallink@ebram.org';
const CONTACT_PHONE = '+852 3792 0707';
const OFFICE_DIRECTORY_URL = 'https://dealmaking.ebram.org/tc/Blog/councillors-office';
const BENEFIT_IMAGES = [
  '/images/index/freeLegalLink/professional-advice.png',
  '/images/index/freeLegalLink/online-consultation.png',
  '/images/index/freeLegalLink/captions-translation.png',
  '/images/index/freeLegalLink/secure-documents.png',
];

const FreeLegalLink = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const { isMobile, isTablet } = useResize();
  const [activeTab, setActiveTab] = useState<'consultation' | 'volunteer'>('consultation');
  const registrationPath = `/${lang}/volunteerRegistration?step=1`;
  const getText = (key: string) => t(key as any) as string;
  const getList = <T,>(key: string) => t(key as any, { returnObjects: true }) as T;
  const introParagraphs = getList<string[]>(`${COPY_KEY}.intro.paragraphs`);
  const highlights = getList<HighlightItem[]>(`${COPY_KEY}.intro.highlights`);
  const schemeParagraphs = getList<string[]>(`${COPY_KEY}.scheme.paragraphs`);
  const serviceAreas = getList<string[]>(`${COPY_KEY}.scheme.serviceAreas`);
  const lawyerCriteria = getList<string[]>(`${COPY_KEY}.criteria.items`);
  const volunteerReasons = getList<VolunteerReason[]>(`${COPY_KEY}.volunteer.reasons`);
  const benefits = getList<DescribedItem[]>(`${COPY_KEY}.consultation.benefits.items`);
  const legalAreas = getList<DescribedItem[]>(`${COPY_KEY}.consultation.areas.items`);
  const exclusions = getList<string[]>(`${COPY_KEY}.consultation.exclusions.items`);
  const eligibility = getList<string[]>(`${COPY_KEY}.consultation.eligibility.items`);
  const steps = getList<DescribedItem[]>(`${COPY_KEY}.consultation.steps.items`);

  return (
    <>
      <Helmet>
        <title>{getText(`${COPY_KEY}.seo.title`)}</title>
        <meta name="description" content={getText(`${COPY_KEY}.seo.description`)} />
      </Helmet>
      <Main>
        <HeroSection>
          <BannerImage
            src={
              lang === 'en'
                ? '/images/index/freeLegalLink/Banner_En.jpg'
                : lang === 'sc'
                ? '/images/index/freeLegalLink/Banner_Sc.jpg'
                : '/images/index/freeLegalLink/Banner_Tc.jpg'
            }
            alt={getText(`${COPY_KEY}.bannerAlt`)}
          />
        </HeroSection>

        <PageContainer isMobile={isMobile} isTablet={isTablet}>
          <TabList role="tablist" aria-label={getText(`${COPY_KEY}.tabs.label`)}>
            <TabButton
              type="button"
              role="tab"
              id="consultation-tab"
              aria-controls="consultation-panel"
              aria-selected={activeTab === 'consultation'}
              active={activeTab === 'consultation'}
              onClick={() => setActiveTab('consultation')}
            >
              {getText(`${COPY_KEY}.tabs.consultation`)}
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              id="volunteer-tab"
              aria-controls="volunteer-panel"
              aria-selected={activeTab === 'volunteer'}
              active={activeTab === 'volunteer'}
              onClick={() => setActiveTab('volunteer')}
            >
              {getText(`${COPY_KEY}.tabs.volunteer`)}
            </TabButton>
          </TabList>

          <div
            role="tabpanel"
            id="consultation-panel"
            aria-labelledby="consultation-tab"
            hidden={activeTab !== 'consultation'}
          >
            <ConsultationLead>
              <Title>{getText(`${COPY_KEY}.consultation.title`)}</Title>
              <Eyebrow>{getText(`${COPY_KEY}.consultation.eyebrow`)}</Eyebrow>
              <BodyText>{getText(`${COPY_KEY}.consultation.introduction`)}</BodyText>
              <BodyText>{getText(`${COPY_KEY}.consultation.summary`)}</BodyText>
              <OfficeButton href={OFFICE_DIRECTORY_URL}>{getText(`${COPY_KEY}.consultation.officeLink`)}</OfficeButton>
            </ConsultationLead>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.consultation.benefits.title`)}</SectionTitle>
              <BenefitGrid>
                {benefits.map((item, index) => (
                  <BenefitItem key={item.title}>
                    <img src={BENEFIT_IMAGES[index]} alt="" />
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </BenefitItem>
                ))}
              </BenefitGrid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.consultation.areas.title`)}</SectionTitle>
              <BodyText>{getText(`${COPY_KEY}.consultation.areas.introduction`)}</BodyText>
              <AreaGrid>
                {legalAreas.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </li>
                ))}
              </AreaGrid>
            </ContentSection>

            <ContentSection>
              <ConditionsGrid>
                <div>
                  <SectionTitle>{getText(`${COPY_KEY}.consultation.exclusions.title`)}</SectionTitle>
                  <ul>
                    {exclusions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <SectionTitle>{getText(`${COPY_KEY}.consultation.eligibility.title`)}</SectionTitle>
                  <ul>
                    {eligibility.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </ConditionsGrid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.consultation.steps.title`)}</SectionTitle>
              <StepsList>
                {steps.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </li>
                ))}
              </StepsList>
              <BodyText>{getText(`${COPY_KEY}.consultation.steps.note`)}</BodyText>
              <OfficeButton href={OFFICE_DIRECTORY_URL}>{getText(`${COPY_KEY}.consultation.officeLink`)}</OfficeButton>
            </ContentSection>
          </div>

          <div role="tabpanel" id="volunteer-panel" aria-labelledby="volunteer-tab" hidden={activeTab !== 'volunteer'}>
            <Box marginBottom={'44px'}>
              <ActionPanel to={registrationPath} aria-label={getText(`${COPY_KEY}.cta`)}>
                <strong>{getText(`${COPY_KEY}.cta`)}</strong>
              </ActionPanel>
            </Box>

            <ContentSection>
              <Eyebrow>{getText(`${COPY_KEY}.intro.eyebrow`)}</Eyebrow>
              <Title>{getText(`${COPY_KEY}.intro.title`)}</Title>
              {introParagraphs.map((item) => (
                <BodyText key={item}>{item}</BodyText>
              ))}

              <FeatureGrid>
                {highlights.map((item) => (
                  <FeatureItem key={item.value}>
                    <FeatureValue>{item.value}</FeatureValue>
                    <FeatureLabel>{item.label}</FeatureLabel>
                  </FeatureItem>
                ))}
              </FeatureGrid>
            </ContentSection>

            <SplitSection isTablet={isTablet}>
              <TextColumn>
                <SectionTitle>{getText(`${COPY_KEY}.scheme.title`)}</SectionTitle>
                {schemeParagraphs.map((item) => (
                  <BodyText key={item}>{item}</BodyText>
                ))}
              </TextColumn>
              <InfoGrid>
                {serviceAreas.map((item) => (
                  <InfoItem key={item}>{item}</InfoItem>
                ))}
              </InfoGrid>
            </SplitSection>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.criteria.title`)}</SectionTitle>
              <InfoGrid>
                {lawyerCriteria.map((item) => (
                  <InfoItem key={item}>{item}</InfoItem>
                ))}
              </InfoGrid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.volunteer.title`)}</SectionTitle>
              <FeatureGrid>
                {volunteerReasons.map((item) => (
                  <FeatureItem key={item.label}>
                    <FeatureValue>{item.label}</FeatureValue>
                    <FeatureLabel>{item.value}</FeatureLabel>
                  </FeatureItem>
                ))}
              </FeatureGrid>
            </ContentSection>

            <ContentSection>
              <SectionTitle>{getText(`${COPY_KEY}.apply.title`)}</SectionTitle>
              <BodyText>{getText(`${COPY_KEY}.apply.paragraph`)}</BodyText>
              <BodyText>
                {getText(`${COPY_KEY}.apply.contact.prefix`)}{' '}
                <ContactLink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</ContactLink>{' '}
                {getText(`${COPY_KEY}.apply.contact.middle`)}{' '}
                <ContactLink href="tel:+85237920707">{CONTACT_PHONE}</ContactLink>{' '}
                {getText(`${COPY_KEY}.apply.contact.suffix`)}
              </BodyText>
              <Box marginTop={'40px'}>
                <ActionPanel to={registrationPath} aria-label={getText(`${COPY_KEY}.cta`)}>
                  <strong>{getText(`${COPY_KEY}.cta`)}</strong>
                </ActionPanel>
              </Box>
            </ContentSection>
          </div>
        </PageContainer>
      </Main>
      <Footer />
    </>
  );
};

export default FreeLegalLink;
